import type { ReactNode } from 'react';
import { type Box, isoMatrix, leftV, rightV, bottomV } from './iso';

/* Shared palette — mirrors the site tokens in app/globals.css. Literal hexes
 * are used where a subtle tint of a token is needed (SVG can't color-mix). */
export const INK = '#14141b';
export const MUTED = '#5b5b68';
export const DIM = '#8c8c99';
export const ROSE = 'var(--color-brand-500)';
export const MONO = 'var(--font-jetbrains)';
export const SIDE = '#e2e1e9'; // side faces of white cards

const nss = { vectorEffect: 'non-scaling-stroke' } as const;

/**
 * Extrusion for a rounded-corner top face, authored in the face's local
 * coordinates: the lower silhouette (left extreme → around both bottom arcs →
 * right extreme) swept down by the depth. A screen-vertical drop of `d` is the
 * local translation (d, d), so the whole side is one path inside the same
 * projection matrix — corners match the top face's arcs exactly.
 */
function roundedExtrusionPath(w: number, h: number, rx: number, d: number): string {
  return [
    `M 0 ${h - rx}`,
    `A ${rx} ${rx} 0 0 0 ${rx} ${h}`,
    `L ${w - rx} ${h}`,
    `A ${rx} ${rx} 0 0 0 ${w} ${h - rx}`,
    `L ${w} ${rx}`,
    `A ${rx} ${rx} 0 0 0 ${w - rx} 0`,
    `L ${w - rx + d} ${d}`,
    `A ${rx} ${rx} 0 0 1 ${w + d} ${rx + d}`,
    `L ${w + d} ${h - rx + d}`,
    `A ${rx} ${rx} 0 0 1 ${w - rx + d} ${h + d}`,
    `L ${rx + d} ${h + d}`,
    `A ${rx} ${rx} 0 0 1 ${d} ${h - rx + d}`,
    'Z',
  ].join(' ');
}

/* ------------------------------------------------------------------ */
/* Generic extruded slab: rounded (single side color, exact corners) or */
/* sharp (two-tone parallelogram sides), plus the projected top face.   */

type SlabProps = {
  box: Box;
  topFill: string;
  stroke?: string;
  rx?: number;
  /** Side color for rounded slabs (rx > 0). */
  sideFill?: string;
  /** Two-tone side colors for sharp slabs (rx = 0). */
  leftFill?: string;
  rightFill?: string;
  /** Optional overlay fill (e.g. gradient url) repainted over the right face. */
  rightOverlay?: string;
  /** Flat 2D content in the top face's local w × h space. */
  children?: ReactNode;
};

export function Slab({
  box,
  topFill,
  stroke = INK,
  rx = 0,
  sideFill = SIDE,
  leftFill = '#0a0a10',
  rightFill = '#232330',
  rightOverlay,
  children,
}: SlabProps) {
  const d = box.depth;

  if (rx > 0) {
    return (
      <g transform={isoMatrix(box)}>
        <path
          d={roundedExtrusionPath(box.w, box.h, rx, d)}
          fill={sideFill}
          stroke={stroke}
          strokeWidth="1"
          style={nss}
          strokeLinejoin="round"
        />
        <rect width={box.w} height={box.h} rx={rx} fill={topFill} stroke={stroke} strokeWidth="1.1" style={nss} strokeLinejoin="round" />
        {children}
      </g>
    );
  }

  const l = leftV(box);
  const r = rightV(box);
  const b = bottomV(box);
  const leftFace = `M ${l.x} ${l.y} L ${b.x} ${b.y} l 0 ${d} L ${l.x} ${l.y + d} Z`;
  const rightFace = `M ${r.x} ${r.y} L ${b.x} ${b.y} l 0 ${d} L ${r.x} ${r.y + d} Z`;
  return (
    <g>
      <path d={leftFace} fill={leftFill} stroke={stroke} strokeWidth="1" style={nss} strokeLinejoin="round" />
      <path d={rightFace} fill={rightFill} stroke={stroke} strokeWidth="1" style={nss} strokeLinejoin="round" />
      {rightOverlay && <path d={rightFace} fill={rightOverlay} stroke="none" />}
      <g transform={isoMatrix(box)}>
        <rect width={box.w} height={box.h} fill={topFill} stroke={stroke} strokeWidth="1.1" style={nss} strokeLinejoin="round" />
        {children}
      </g>
    </g>
  );
}

/** Flat ground shadow: the top-face rhombus offset onto the ground plane. */
export function Shadow({ box, rx = 0 }: { box: Box; rx?: number }) {
  return (
    <g transform={`translate(14 ${box.depth + 12})`} opacity="0.55">
      <g transform={isoMatrix(box)}>
        <rect width={box.w} height={box.h} rx={rx} fill="rgba(16,16,28,0.08)" />
      </g>
    </g>
  );
}

/* ------------------------------------------------------------------ */
/* Scene objects. Each renders its Slab + flat content; the wrapping    */
/* animation groups live in IsoScene.                                   */

export function RouterPlate({ box }: { box: Box }) {
  return (
    <Slab
      box={box}
      topFill={INK}
      leftFill="#0a0a10"
      rightFill="#232330"
      stroke="#08080d"
      rightOverlay="url(#iso-edge-grad)"
    >
      {/* Inner border, engraved */}
      <rect x="12" y="12" width={box.w - 24} height={box.h - 24} rx="6" fill="none" stroke="rgba(255,255,255,0.13)" strokeWidth="1" />
      {/* Corner screws */}
      {[
        [26, 26],
        [box.w - 26, 26],
        [26, box.h - 26],
        [box.w - 26, box.h - 26],
      ].map(([x, y]) => (
        <g key={`${x}-${y}`}>
          <circle cx={x} cy={y} r="5.5" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
          <circle cx={x} cy={y} r="1.6" fill="rgba(255,255,255,0.3)" />
        </g>
      ))}
      {/* Wordmark */}
      <text x="34" y="132" fill="rgba(255,255,255,0.45)" fontSize="12" fontWeight="600" letterSpacing="3.5">
        ROUTING CORE
      </text>
      <text x="32" y="176" fill="#ffffff" fontSize="42" fontWeight="800" letterSpacing="-1">
        rii.link
      </text>
      {/* Socket (CPU-like) */}
      <rect x="238" y="128" width="62" height="42" rx="5" fill="#1f1f2b" stroke="rgba(255,255,255,0.16)" strokeWidth="1" />
      <circle cx="269" cy="149" r="9" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="1" />
      {/* Circuit traces, top-right corner region */}
      <g fill="none" stroke={ROSE} strokeWidth="1.5" opacity="0.4">
        <path d="M 214 44 H 264 V 78 H 300" />
        <path d="M 214 60 H 248 V 96" />
        <path d="M 292 44 V 62 H 312" />
      </g>
      <g fill={ROSE} opacity="0.55">
        <circle cx="214" cy="44" r="3" />
        <circle cx="214" cy="60" r="3" />
        <circle cx="300" cy="78" r="3" />
        <circle cx="248" cy="96" r="3" />
        <circle cx="312" cy="62" r="3" />
      </g>
      {/* Status LED */}
      <g data-part="led">
        <circle cx="298" cy="248" r="12" fill="rgba(250,67,127,0.22)" />
        <circle cx="298" cy="248" r="5" fill={ROSE} />
      </g>
      {/* Engraved routing glyph: three diverging traces, bottom-left */}
      <g fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="1.25">
        <path d="M 34 226 H 76 L 108 258" />
        <path d="M 34 240 H 96" />
        <path d="M 34 254 H 76 L 108 222" />
      </g>
    </Slab>
  );
}

export function McpChip({ box }: { box: Box }) {
  const pinFill = '#cfcdd8';
  return (
    <g>
      {/* Pins first so the chip body overlaps their inner ends */}
      <g transform={isoMatrix(box)}>
        <g fill={pinFill} stroke="#8f8d9c" strokeWidth="0.75">
          {[24, 52, 80].map((p) => (
            <g key={p}>
              <rect x={p} y="-9" width="12" height="9" rx="1.5" />
              <rect x={p} y={box.h} width="12" height="9" rx="1.5" />
              <rect x="-9" y={p} width="9" height="12" rx="1.5" />
              <rect x={box.w} y={p} width="9" height="12" rx="1.5" />
            </g>
          ))}
        </g>
      </g>
      <Slab box={box} topFill="#191922" leftFill="#0a0a10" rightFill="#242430" stroke="#08080d">
        <rect x="9" y="9" width={box.w - 18} height={box.h - 18} rx="3" fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="1" />
        <circle cx="20" cy="20" r="2.5" fill="rgba(255,255,255,0.35)" />
        <text x="38" y="66" fill="#ffffff" fontSize="26" fontWeight="800" letterSpacing="0.5">
          MCP
        </text>
        <text x="55" y="90" fill={ROSE} fontSize="15" textAnchor="middle">
          ✦
        </text>
      </Slab>
    </g>
  );
}

export function TerminalTile({ box }: { box: Box }) {
  return (
    <Slab box={box} topFill="#ffffff" rx={9}>
      {/* Title bar (squared bottom corners) */}
      <path d={`M 9 0.5 H ${box.w - 9} A 8.5 8.5 0 0 1 ${box.w - 0.5} 9 V 27 H 0.5 V 9 A 8.5 8.5 0 0 1 9 0.5 Z`} fill={INK} />
      <circle cx="15" cy="14" r="3.5" fill={ROSE} />
      <circle cx="29" cy="14" r="3.5" fill="rgba(255,255,255,0.28)" />
      <circle cx="43" cy="14" r="3.5" fill="rgba(255,255,255,0.28)" />
      <text x="14" y="60" fill={INK} fontSize="12" style={{ fontFamily: MONO }}>
        $ curl api.rii.link/v1
      </text>
      <rect x="177" y="50" width="6" height="12" fill={ROSE} data-part="cursor" />
      <text x="14" y="84" fill={DIM} fontSize="11.5" style={{ fontFamily: MONO }}>
        201 · created
      </text>
      <rect x="14" y="96" width="76" height="7" rx="3.5" fill="#f0eff4" />
    </Slab>
  );
}

/* ------------------------------------------------------------------ */
/* Billboard label: screen-facing mono caption with a short leader line. */

export function IsoLabel({ x, y, text, lead }: { x: number; y: number; text: string; lead?: { y1: number; y2: number } }) {
  return (
    <g data-iso-label="">
      {lead && <line x1={x} y1={lead.y1} x2={x} y2={lead.y2} stroke="rgba(16,16,28,0.25)" strokeWidth="1" style={nss} />}
      <text
        x={x}
        y={y}
        fill={DIM}
        fontSize="13"
        letterSpacing="2"
        textAnchor="middle"
        style={{ fontFamily: MONO }}
      >
        {text}
      </text>
    </g>
  );
}

/** White satellite tile: a product mark above its name. `children` is the
 *  mark (a simple-icons or lucide component) — nested <svg> inside the
 *  projected face is fine, the old IntegrationsTile did the same. Pass
 *  `markSize` matching the mark's `size` prop so it centers exactly
 *  (the old hardcoded 34 left 30px marks 2px off, #224). */
export function BrandTile({
  box,
  name,
  markSize = 34,
  children,
}: {
  box: Box;
  name: string;
  markSize?: number;
  children: ReactNode;
}) {
  return (
    <Slab box={box} topFill="#ffffff" rx={9}>
      <g transform={`translate(${box.w / 2 - markSize / 2}, ${box.h / 2 - 30})`}>{children}</g>
      <text x={box.w / 2} y={box.h - 16} textAnchor="middle" fill={INK} fontSize="13" fontWeight="700">
        {name}
      </text>
    </Slab>
  );
}
